import { cloneDeep } from 'lodash';
import React from 'react';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback, formatDate, formatTime } from '../../util';
import { CoachMessage } from '../CoachMessage';
import { NextButton } from '../Button';

import { MultipleDisplayEnum } from './MultipleDisplayEnum';
import { MultipleDisplay } from './MultipleDisplay';

/*
  ! MultipleDisplaysComposite
*/

export interface MultipleDisplayCompositeComponent extends BaseComponent {
  type: 'multipleDisplayComposite';
  props: MultipleDisplayCompositeProps;
}
export interface MultipleDisplayCompositeProps {
  message: string;
  messages?: string[];
  bubbleType: MultipleDisplayEnum;
  buttonText: string;
  isEcho?: boolean;
}

export const MultipleDisplayComposite: React.FC<MultipleDisplayCompositeProps> = ({
  message,
  messages,
  bubbleType,
  buttonText,
  isEcho,
}) => {
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <CoachMessage message={message} />
      </div>
      {messages && messages.length > 0
        ? messages.map((message: string, index: number) => {
            return (
              <div
                style={{ marginBottom: '1rem' }}
                key={`MultipleDisplayComposite_EnclosingDiv_${index}`}
              >
                <MultipleDisplay
                  message={message}
                  bubbleType={bubbleType}
                  key={`MultipleDisplayComposite_${index}`}
                />
              </div>
            );
          })
        : ''}
      {!isEcho ? <NextButton buttonText={buttonText} /> : ''}
    </>
  );
};

/*
  ! renderMultipleDisplayCompositeAction

  snippets from previous inputs, e.g. MultipleInput, are passed here and are displayed
  as composite => CoachMessage + MultipleDisplay + Button
*/
export const renderMultipleDisplayCompositeAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;

    let messages = payload.messages;
    messages = getValueFrom ? context.userData[getValueFrom] : messages;

    // note: undefined messages are handled within component!
    if (typeof messages === 'string') {
      messages = [messages]; // make an array
    }

    // if there was no variable defined in getValueFrom or if the variable
    // does not exist, messages by be undefined ==> in that case, nothing is
    // rendered
    let list_of_messages = [''];
    // there was something in getValueFrom
    if (messages) {
      // check MultipleDisplayComposite.state.tsx in creator for details what types are supported
      /*
        ReadWriteType.stringArray,
        ReadWriteType.typedObject,
        ReadWriteType.string,
      */
      // may be an array of arrays, especially in case of loops
      messages = messages.flat();
      // if messages is a list of strings (also a list of length 1), then return
      if (messages.length > 0 && typeof messages[0] === 'string') {
        list_of_messages = messages;
      } else {
        // otherwise, we have a typed object
        // parse types and return value based on type
        list_of_messages = messages.map((message: any) => {
          const messagetype = (message as Record<string, any>)['type'];

          if (messagetype === 'time' && message.time && message.time !== '') {
            message.value = ', ' + formatTime(message.time, intl.locale);
          } else {
            if (messagetype === 'date' && message.date && message.date !== '') {
              message.value = ', ' + formatDate(message.date, intl.locale);
            }
          }
          return message.value;
        });
      }
    }

    newContext.widgetData.push({
      type: 'multipleDisplayComposite',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        message: translateWithFallback(intl, payload.message),
        messages: list_of_messages,
        bubbleType: payload.bubbleType,
        buttonText: translateWithFallback(intl, payload.buttonText),
        isEcho: payload.isEcho,
      },
    });

    return newContext;
  });
};
